/**
 * SettingsPixels — Iter 191t · Marketing › Pixels & Tags
 *
 * Squarespace-parity page for wiring third-party ad + analytics pixels.
 * Supported: Meta Pixel (Facebook/Instagram), Google Analytics 4,
 * Google Ads, TikTok, Pinterest — plus a free-form "custom code" slot
 * (head / body) for anything else.
 *
 * Persistence:
 *   GET  /api/builder/settings          → returns `pixels`
 *   PUT  /api/builder/settings { pixels: {...} }
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Target, Save, Loader2, ExternalLink, Code2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { GlassCard, DashboardBackdrop, GradientIconTile } from '@/components/dashboard/dashboard-ui';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULTS = {
  meta_pixel_id: '',
  ga4_measurement_id: '',
  google_ads_id: '',
  google_ads_label: '',
  tiktok_pixel_id: '',
  pinterest_tag_id: '',
  custom_head_code: '',
  custom_body_code: '',
};

const PROVIDERS = [
  {
    key: 'meta_pixel_id',
    name: 'Meta Pixel',
    sub: 'Facebook & Instagram Ads',
    placeholder: 'e.g. 123456789012345',
    docs: 'https://business.facebook.com/events-manager',
    docsLabel: 'Meta Events Manager',
    format: 'Numeric Pixel ID',
    color: '#1877F2',
  },
  {
    key: 'ga4_measurement_id',
    name: 'Google Analytics 4',
    sub: 'Traffic & conversions',
    placeholder: 'e.g. G-XXXXXXXXXX',
    docs: 'https://analytics.google.com',
    docsLabel: 'Google Analytics',
    format: 'Measurement ID (G-…)',
    color: '#F9AB00',
  },
  {
    key: 'google_ads_id',
    name: 'Google Ads',
    sub: 'Search / Display / Shopping',
    placeholder: 'e.g. AW-123456789',
    docs: 'https://ads.google.com',
    docsLabel: 'Google Ads',
    format: 'Conversion ID (AW-…)',
    color: '#4285F4',
    extra: {
      key: 'google_ads_label',
      label: 'Conversion label (optional)',
      placeholder: 'e.g. abcDefGhi123',
    },
  },
  {
    key: 'tiktok_pixel_id',
    name: 'TikTok Pixel',
    sub: 'TikTok Ads',
    placeholder: 'e.g. C4A0B123XXXXX',
    docs: 'https://ads.tiktok.com',
    docsLabel: 'TikTok Events Manager',
    format: 'Pixel Code',
    color: '#FE2C55',
  },
  {
    key: 'pinterest_tag_id',
    name: 'Pinterest Tag',
    sub: 'Pinterest Ads',
    placeholder: 'e.g. 2612345678901',
    docs: 'https://ads.pinterest.com',
    docsLabel: 'Pinterest Ads',
    format: 'Tag ID',
    color: '#E60023',
  },
];

export default function SettingsPixels() {
  const [pixels, setPixels] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPixels({ ...DEFAULTS, ...(data?.pixels || {}) });
      } catch (_) {
        /* non-fatal */
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/builder/settings`,
        { pixels },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Pixels saved');
    } catch {
      toast.error('Could not save pixels');
    } finally {
      setSaving(false);
    }
  };

  const set = (key, val) => setPixels((p) => ({ ...p, [key]: val }));

  return (
    <div className="relative" data-testid="settings-pixels-page">
      <DashboardBackdrop />
      <div className="max-w-3xl mx-auto py-10 px-4 space-y-6">
        <BackToSettings />

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA]">Brand &amp; Marketing</p>
            <h1 className="text-3xl font-bold text-white font-syne mt-1">Pixels &amp; Tags</h1>
            <p className="text-sm text-white/65 mt-2 max-w-xl">
              Fire tracking pixels for your ad networks and analytics tools. Paste an ID and we&rsquo;ll
              inject the correct script snippet across every page of your storefront and checkout.
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving || loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all"
            data-testid="pixels-save-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        {loading ? (
          <div className="text-white/50 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {PROVIDERS.map((p) => (
              <GlassCard key={p.key} className="p-6" data-testid={`pixel-card-${p.key}`}>
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-11 h-11 rounded-xl grid place-items-center flex-shrink-0"
                    style={{ background: `${p.color}22`, border: `1px solid ${p.color}44` }}
                  >
                    <Target size={16} style={{ color: p.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-white font-bold font-syne text-base">{p.name}</h2>
                    <p className="text-[11.5px] text-white/55 mt-0.5">{p.sub} · {p.format}</p>
                  </div>
                  <a
                    href={p.docs}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#A78BFA] hover:text-white inline-flex items-center gap-1"
                  >
                    {p.docsLabel} <ExternalLink size={11} />
                  </a>
                </div>
                <div className="space-y-3">
                  <Input
                    value={pixels[p.key] || ''}
                    onChange={(e) => set(p.key, e.target.value.trim())}
                    placeholder={p.placeholder}
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl font-mono text-[13px]"
                    data-testid={`pixel-input-${p.key}`}
                  />
                  {p.extra && (
                    <div>
                      <Label className="text-[#94A3B8] text-xs">{p.extra.label}</Label>
                      <Input
                        value={pixels[p.extra.key] || ''}
                        onChange={(e) => set(p.extra.key, e.target.value.trim())}
                        placeholder={p.extra.placeholder}
                        className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl font-mono text-[13px]"
                        data-testid={`pixel-input-${p.extra.key}`}
                      />
                    </div>
                  )}
                </div>
              </GlassCard>
            ))}

            {/* Custom head/body script slots */}
            <GlassCard className="p-6" data-testid="pixel-card-custom">
              <div className="flex items-start gap-4 mb-4">
                <GradientIconTile icon={Code2} ramp="violet" />
                <div className="flex-1">
                  <h2 className="text-white font-bold font-syne text-base">Custom code injection</h2>
                  <p className="text-[11.5px] text-white/55 mt-0.5">
                    Anything else — Klaviyo, Snapchat, LinkedIn Insight, Hotjar, chat widgets.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-[#94A3B8] text-xs">
                    Head code — injected inside <span className="font-mono text-white/70">&lt;head&gt;</span>
                  </Label>
                  <Textarea
                    rows={5}
                    value={pixels.custom_head_code || ''}
                    onChange={(e) => set('custom_head_code', e.target.value)}
                    placeholder="<script>/* head snippet */</script>"
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl font-mono text-xs"
                    data-testid="pixel-custom-head"
                  />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">
                    Body code — injected before <span className="font-mono text-white/70">&lt;/body&gt;</span>
                  </Label>
                  <Textarea
                    rows={5}
                    value={pixels.custom_body_code || ''}
                    onChange={(e) => set('custom_body_code', e.target.value)}
                    placeholder="<!-- Klaviyo, chat widgets, etc. -->"
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl font-mono text-xs"
                    data-testid="pixel-custom-body"
                  />
                </div>
              </div>
            </GlassCard>
          </>
        )}
      </div>
    </div>
  );
}
